import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Card, Space, notification, Typography, Flex, List, Modal, Input, Skeleton, Pagination } from 'antd';
import { PlusOutlined, CloseOutlined } from '@ant-design/icons';
import { createVenue, getVenues, deleteVenue, updateVenue } from '../../api/venue'; // Assuming API functions are in this path  
import VenueForm from '../../components/Venues/venueForm'; // Assuming you have a VenueForm component  
import { openNotificationWithIcon } from '../../utils/notificationUtils';
import { useSettings } from '../../contexts/settingsContext';

const { Title } = Typography;

const Venues = () => {
    const { state: settings } = useSettings();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [venues, setVenues] = useState([]);
    const [selectedVenue, setSelectedVenue] = useState(null);
    const [error, setError] = useState(null);
    const [isPopupVisible, setIsPopupVisible] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [filterName, setFilterName] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Fetch venues  
    const fetchVenues = useCallback(async () => {
        setLoading(true);
        try {
            const venuesData = await getVenues();
            setVenues(venuesData);
        } catch (err) {
            console.error("Error fetching venues", err);
            setError("Failed to load venues. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch data on initial render  
    useEffect(() => {
        fetchVenues();
    }, [fetchVenues]);

    // Open venue form popup  
    const openPopup = useCallback((venue) => {
        setSelectedVenue(venue);
        setIsPopupVisible(true);
        setIsCreating(!venue);
    }, []);

    const closePopup = useCallback(() => {
        setIsPopupVisible(false);
    }, []);

    // Handle venue creation/updation  
    const handleNewVenueSubmit = useCallback(async (values) => {
        setSubmitting(true);
        try {
            if (isCreating) {
                await createVenue(values);
                openNotificationWithIcon("success", "Venue Created", "The venue has been successfully created!");
            } else {
                // Assuming updateVenue is implemented similarly to createVenue  
                await updateVenue(selectedVenue._id, values);
                openNotificationWithIcon("success", "Venue Updated", "The venue has been successfully updated!");
            }
            await fetchVenues();
            closePopup();
        } catch (err) {
            console.error("Error creating/updating venue", err);
            openNotificationWithIcon("error", "Venue Operation Failed", `Failed to ${isCreating ? "create" : "update"} venue. ${err.message}`);
        } finally {
            setSubmitting(false);
        }
    }, [isCreating, selectedVenue, fetchVenues, closePopup]);

    // Handle venue deletion  
    const handleDeleteVenue = useCallback(async (venueId) => {
        setLoading(true);
        try {
            await deleteVenue(venueId);
            openNotificationWithIcon("success", "Venue Deleted", "The venue has been successfully deleted!");
            await fetchVenues();
        } catch (err) {
            console.error("Error deleting venue", err);
            openNotificationWithIcon("error", "Venue Deletion Failed", `Failed to delete venue. ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [fetchVenues]);

    const confirmDeleteVenue = useCallback((venueId) => {
        Modal.confirm({
            title: "Are you sure you want to delete this venue?",
            content: "This action cannot be undone.",
            okText: "Yes",
            okType: "danger",
            cancelText: "No",
            onOk: () => handleDeleteVenue(venueId),
        });
    }, [handleDeleteVenue]);

    // Memoized filtering and pagination for venues  
    const filteredVenues = useMemo(() => {
        return venues.filter(
            (item) => item.venue && item.venue.toLowerCase().includes(filterName.toLowerCase())
        );
    }, [venues, filterName]);

    const venuesToShow = useMemo(() => {
        const startIndex = (page - 1) * pageSize;
        const endIndex = page * pageSize;
        return filteredVenues.slice(startIndex, endIndex);
    }, [filteredVenues, page, pageSize]);

    if (error) {
        return <div className="error-message">{error}</div>;
    }

    return (
        <div>
            <Flex style={{ marginBottom: "15px", height: "50px" }} align="center" justify="space-between">
                <Title level={4} style={{ margin: 0 }}>
                    Venues
                </Title>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => openPopup(null)}>
                    Create Venue
                </Button>
            </Flex>
            <Card bordered={false}>
                <Space direction="vertical" size={"middle"} style={{ width: "100%" }}>
                    <Input
                        placeholder="Filter by name"
                        value={filterName}
                        onChange={(e) => {
                            setFilterName(e.target.value);
                            setPage(1);
                        }}
                    />
                    {loading ? (
                        <Skeleton active />
                    ) : (
                        <>
                            <List
                                bordered
                                dataSource={venuesToShow}
                                renderItem={(item) => (
                                    <List.Item
                                        actions={[
                                            <Button
                                                type="link"
                                                icon={<CloseOutlined />}
                                                onClick={() => confirmDeleteVenue(item._id)}
                                            />,
                                        ]}
                                    >
                                        {item.venue}
                                    </List.Item>
                                )}
                            />
                            <Pagination
                                current={page}
                                pageSize={pageSize}
                                total={filteredVenues.length}
                                onChange={(page, pageSize) => {
                                    setPage(page);
                                    setPageSize(pageSize);
                                }}
                                showSizeChanger
                            />
                        </>
                    )}
                </Space>
            </Card>
            <VenueForm
                visible={isPopupVisible}
                onCancel={closePopup}
                onSubmit={handleNewVenueSubmit}
                selectedVenue={selectedVenue}
                submitting={submitting}
            />
        </div>
    );
};

export default Venues;  